import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";

interface CursorTooltipProps {
  text?: string;
  children: React.ReactNode;
  disabled?: boolean;
}

const TooltipOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  background-color: #232129;
  color: white;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 12px;
  max-width: 300px;
  line-height: 1.4;
  z-index: 10000;
  font-family: "Sharp Sans", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, sans-serif;
  font-weight: 400;
  pointer-events: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.1);
  will-change: transform;
  backface-visibility: hidden;
  contain: layout style paint;
  isolation: isolate;
  transform-style: preserve-3d;
  transform: translate3d(0, 0, 0);
  visibility: hidden;
  /* Prevent font from being reloaded */
  font-display: block;
  text-rendering: geometricPrecision;
  /* Force font to load and cache */
  &::before {
    content: "";
    font-family: "Sharp Sans";
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }
`;

const InteractionWrapper = styled.div`
  position: relative;
  display: contents;
`;

const CursorTooltip: React.FC<CursorTooltipProps> = ({
  text,
  children,
  disabled,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const timeoutRef = useRef<number | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let animationFrameId: number;

    const handleMouseMove = (e: MouseEvent) => {
      // Cancel previous animation frame if it exists
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }

      // Use requestAnimationFrame to throttle updates
      animationFrameId = requestAnimationFrame(() => {
        setMousePosition({ x: e.clientX, y: e.clientY });
      });
    };

    if (showTooltip) {
      document.addEventListener("mousemove", handleMouseMove, {
        passive: true,
      });
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [showTooltip]);

  // Update tooltip position using direct DOM manipulation to prevent re-renders
  useEffect(() => {
    if (tooltipRef.current && showTooltip) {
      const tooltip = tooltipRef.current;
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let offsetX = 15;
      let offsetY = -10;

      // Adjust if tooltip would go off right edge
      if (mousePosition.x + 315 > viewportWidth) {
        offsetX = -315;
      }

      // Adjust if tooltip would go off bottom edge
      if (mousePosition.y + 60 > viewportHeight) {
        offsetY = -60;
      }

      // Use direct transform manipulation to avoid re-renders and font reloading
      const x = mousePosition.x + offsetX;
      const y = mousePosition.y + offsetY;

      // Apply styles directly to prevent component re-creation
      tooltip.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      tooltip.style.willChange = "transform";
      tooltip.style.visibility = "visible";
    }
  }, [mousePosition, showTooltip]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!text || disabled) {
    return <>{children}</>;
  }

  const handleMouseEnter = () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Small delay to prevent tooltips on brief mouseovers
    timeoutRef.current = window.setTimeout(() => {
      setShowTooltip(true);
    }, 200);
  };

  const handleMouseLeave = () => {
    // Clear timeout if mouse leaves before tooltip appears
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Hide tooltip immediately
    if (tooltipRef.current) {
      tooltipRef.current.style.visibility = "hidden";
    }

    setShowTooltip(false);
  };

  return (
    <InteractionWrapper
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      {showTooltip && <TooltipOverlay ref={tooltipRef}>{text}</TooltipOverlay>}
    </InteractionWrapper>
  );
};

export default CursorTooltip;
